import { FileSelect } from '@modules/common/components/FileSelect';
import { FileUploadModal } from '@modules/integrationsV2/components/FileUploadModal';
import { useUploadCSVFile } from '@modules/integrationsV2/hooks';
import { SelectedFileError } from '@modules/integrationsV2/types';
import { Box, ButtonProps, Typography } from '@mui/material';
import { IntegrationType } from 'generated/graphql';
import { useState } from 'react';

const MAX_FILE_SIZE = 350_000_000; // 350 MB

interface FileUploaderProps {
  id: string;
  buttonProps?: Pick<ButtonProps, 'variant' | 'size'>;
  integrationType?: IntegrationType;
  buttonLabel?: string;
  isReupload?: boolean;
}

export const FileUploader = ({
  id,
  buttonProps,
  integrationType = IntegrationType.Feedback,
  buttonLabel,
  isReupload,
}: FileUploaderProps) => {
  const [selectedFile, setSelectedFile] = useState<File>();
  const [selectedFileName, setSelectedFileName] = useState<string>();
  const [selectedFileError, setSelectedFileError] =
    useState<SelectedFileError>();

  const [
    uploadCSVFile,
    { fileURL: CSVFileURL, s3Key, isCompleted, progress, cancelFileUpload },
  ] = useUploadCSVFile();

  const onFileSelect = async (selectedFile: File | undefined) => {
    // Do nothing if no file has been selected.
    if (!selectedFile) {
      return;
    }

    setSelectedFileName(selectedFile.name);

    // Prevent uploading of file greater than 100MB.
    if (selectedFile.size > MAX_FILE_SIZE) {
      const selectedFileSizeInMB = (selectedFile.size / 1_000_000).toFixed(1);
      const maxFileSizeInMB = (MAX_FILE_SIZE / 1_000_000).toFixed(0);

      setSelectedFileError({
        message: (
          <Typography>
            The max size supported for a file is{' '}
            <Box component="span" fontWeight={600}>
              {maxFileSizeInMB}MB
            </Box>
            . The uploaded file is larger (
            <Box component="span" fontWeight={600}>
              {selectedFileSizeInMB}MB
            </Box>
            ). You can upload your data by splitting your file into smaller
            files.
          </Typography>
        ),
        errorType: 'file_size_exceeded',
      });

      setSelectedFile(undefined);
      return;
    }

    setSelectedFile(selectedFile);
    // Start uploading the CSV file
    uploadCSVFile({
      file: selectedFile,
      integrationID: id,
      onError: () => {
        setSelectedFileError({
          message:
            'An error occurred while uploading the file. Please try again.',
          errorType: 'other',
        });
      },
    });
  };

  const onCloseDialog = () => {
    setSelectedFile(undefined);
    setSelectedFileName(undefined);
    setSelectedFileError(undefined);
    cancelFileUpload();
  };

  return (
    <>
      <FileSelect
        id={id}
        acceptFileType=".csv"
        onFileSelect={onFileSelect}
        buttonProps={buttonProps}
        buttonLabel={buttonLabel}
      />
      {selectedFileName && (
        <FileUploadModal
          open
          selectedFile={selectedFile}
          selectedFileName={selectedFileName}
          selectedFileError={selectedFileError}
          CSVFileURL={CSVFileURL}
          s3Key={s3Key}
          integrationID={id}
          integrationType={integrationType}
          onCloseDialog={onCloseDialog}
          uploadProgress={progress}
          isFileUploadCompleted={isCompleted}
          onFileSelect={onFileSelect}
          isReupload={isReupload}
        />
      )}
    </>
  );
};
